import React from 'react';
import PropTypes from 'prop-types';
import { Flex, Spacer } from '@chakra-ui/react';
import { observer } from 'mobx-react';
import { withNamespaces } from 'react-i18next';

import colors from 'styles/themes/wikifactory/styles/colors';
import Heading from 'components/Heading';
import Button from 'components/Button';
import Paragraph from 'components/Paragraph';
import Icon from 'components/Icon';

import styled from 'styled-components';

import {
  fileFormatsToReadableText,
  getMessageFromRequirement,
} from 'apps/rfq/fileRequirementsUtils';

import useFileInput from 'libs/useFileInput';

const IncompleteRFQSection = observer(({ rfqManager }) => {
  const { fileRequirements } = rfqManager.rfqApp;

  let fileFormatRequirements = [];
  let supportingFilesRequirements = [];
  if (fileRequirements) {
    fileFormatRequirements = fileRequirements.filter(
      fReq => fReq.type === 'FILE_FORMAT',
    );
    supportingFilesRequirements = fileRequirements.filter(
      fReq => fReq.type === 'SUPPORTING_FILE',
    );
  }

  let acceptPrimaryFileFormats = '';
  if (fileFormatRequirements.length > 0) {
    acceptPrimaryFileFormats = fileFormatRequirements[0].formats.join(',');
  }
  const selectPrimaryFile = useFileInput(
    fileList => {
      if (fileList.length > 0) rfqManager.UpdatePrimaryFile(fileList[0]);
    },
    { multiple: false, accept: acceptPrimaryFileFormats },
  );

  const acceptSupportingFilesFormats = supportingFilesRequirements.reduce(
    (acc, fileRequirement) => {
      return [...acc, ...fileRequirement.formats];
    },
    [],
  );
  const selectSupportingFiles = useFileInput(
    fileList => {
      if (fileList.length > 0) rfqManager.AddSupportingFiles(fileList);
    },
    {
      accept: acceptSupportingFilesFormats.join(','),
    },
  );

  let title = '';
  let message = '';
  let buttonText = '';
  let onClick = null;
  let messages = null;
  let buttonIcon = 'upload';
  const isAddressMissing = !rfqManager.rfqApp.address;
  if (isAddressMissing) {
    title = `Delivery Address required`;
    message = `Please provide a delivery address so that we can include shipping costs in your final quote.`;
    buttonText = `Add Address`;
    onClick = () => {
      rfqManager.BeginChangeAddress();
    };
    buttonIcon = 'add';
  } else if (fileFormatRequirements.length > 0) {
    // NOTE (david): There should ever be at most 1 FILE_FORMAT requirements.
    const fileFormatRequirement = fileFormatRequirements[0];
    const formatsText = fileFormatsToReadableText(
      fileFormatRequirement.formats,
    );

    title = `${formatsText} File Required for your RFQ`;

    const forText = fileFormatRequirement.for.name;
    message = `The file format you uploaded as your primary RFQ file is not valid for ${forText}. Please upload a ${formatsText} file.`;

    buttonText = 'Replace';
    onClick = selectPrimaryFile;
  } else if (supportingFilesRequirements.length > 0) {
    buttonText = 'Upload';
    onClick = selectSupportingFiles;
    if (supportingFilesRequirements.length > 1) {
      title = 'Supporting Files Required';
      const items = supportingFilesRequirements.map(fileRequirement => (
        <li key={fileRequirement.id}>
          <Paragraph>{getMessageFromRequirement(fileRequirement)}</Paragraph>
        </li>
      ));
      messages = <MessagesList>{items}</MessagesList>;
    } else {
      // only 1 requirement
      title = 'Supporting File Required';
      message = getMessageFromRequirement(supportingFilesRequirements[0]);
    }
  } else {
    // Ideally this should not happen and the parent should just not render this component.
    return null;
  }

  return (
    <IncompleteRFQSectionStyled>
      <Flex alignItems='center'>
        <Icon name='info' color={colors.wfOrange} size='medium' />
        <Heading margin='0 0 0 small'>{title}</Heading>
      </Flex>
      <Flex alignItems='center'>
        <MessageWrapper>
          {messages || <Paragraph>{message}</Paragraph>}
        </MessageWrapper>
        <Spacer />
        <div>
          <Button
            text={buttonText}
            variant='tertiary'
            outline
            size='small'
            icon={buttonIcon}
            iconPosition='right'
            transparent
            onClick={onClick}
          />
        </div>
      </Flex>
    </IncompleteRFQSectionStyled>
  );
});

IncompleteRFQSection.propTypes = {
  rfqManager: PropTypes.object,
};

const IncompleteRFQSectionStyled = styled.div`
  margin-top: ${props => props.theme.spacing.default};
  padding: ${props => props.theme.spacing.default};
  border: 1px solid ${props => props.theme.color.wfOrange};
  background-color: ${props => props.theme.color.wfOrange200};
  align-self: stretch;
`;

const MessageWrapper = styled.div`
  margin-right: ${props => props.theme.spacing.medium};
  margin-top: ${props => props.theme.spacing.small};
`;

const MessagesList = styled.ul`
  padding-left: ${props => props.theme.spacing.large};
  list-style-type: initial;
  list-style-position: outside;
`;

export default withNamespaces()(IncompleteRFQSection);
